<!--

This component is used to wrap the JSONForms components and add some custom styling to it.

TODO: features to do on rewrite
  - each component should hold a previous value so it can be reset to it

-->

<template>
  <div
    v-if="renderMe"
    class="custom-wrapper"
    :class="{
      'half-top-gap': schema.child,
      'no-top-gap': ['action-buttons'].includes(schema.type) || schema.topSpacing === 'none',
    }"
    :style="styleProps">
    <!-- Title -->
    <div
      v-if="title"
      class="flex" >
      <div
        v-if="title"
        v-b-toggle="collapseId"
        class="title greedy ellipsis flex flex-align-center"
        :class="[schema.titleType, { 'no-margin': ['group-title'].includes(schema.type) || (schema.collapse && !collapsed), collapsable: schema.collapse, 'not-collapsable': !schema.collapse }]" >{{ title }}
        <template v-if="['remoteData'].includes(schema.type) && count !== null && total !== null">
          <DotSeparator
            :color="SBColors.blackUltralight"
            :size="4"
            style="margin-left: 8px; margin-right: 8px;" />
          <p class="count" ><span class="lighter">{{ count | bigDigits }}</span> / {{ total | bigDigits }}</p>
        </template>
        <SBIcon
          v-if="schema.collapse"
          class="mx-2"
          :icon="SBIconList.DOWN_ARROW"
          :color="SBColors.greyPrimary"
          :size="10" />
      </div>
      <!-- <div class="greedy" /> -->
      <!-- Do not show "optional" if buttons type is being rendered. -->
      <div
        v-if="!schema.hideOptional && !required && !['remoteData', 'action-buttons', 'dividerFull', 'divider', 'skeleton', 'group-title'].includes(schema.type)"
        class="optional">Optional</div>
      <div
        v-if="schema.modalInfo"
        class="modal-info flex flex-align-center show-hand ml-2"
        @click="showInfoModal" >
        <svg
          v-if="schema.modalInfo.buttonPrefixIcon && typeof schema.modalInfo.buttonPrefixIcon === 'string' && schema.modalInfo.buttonPrefixIcon.endsWith('.svg')"
          class="dot svg mr-1 external-svg"
          :data-src="schema.modalInfo.buttonPrefixIcon"
          style="width: 12px; height: 12px;" />
        <img
          v-else-if="schema.modalInfo.buttonPrefixIcon && typeof schema.modalInfo.buttonPrefixIcon === 'string'"
          :src="schema.modalInfo.buttonPrefixIcon"
          style="width: 12px; height: 12px;"
          class="mr-1"
        >
        <span>{{ schema.modalInfo.buttonText }}</span>
      </div>
    </div>
    <!-- Subtitle prefix -->
    <div
      v-if="schema.subtitleTop"
      class="subtitle-top flex flex-align-center">
      <img
        v-if="schema.subtitleTopPrefixIcon && typeof schema.subtitleTopPrefixIcon === 'string'"
        :src="schema.subtitleTopPrefixIcon"
        style="width: 10px; height: 10px;"
        class="mr-1"
      >
      <SBIcon
        v-else-if="!schema.noSubtitlePrefixIcon"
        class="mr-1"
        :icon="SBIconList.INFO"
        :color="SBColors.greyLight"
        :size="10" />
      <span>{{ schema.subtitleTop }}</span>
    </div>
    <!-- SLOT -->
    <div
      :class="{ 'no-border': schema.noBorder || wrapInToggle }">
      <template
        v-if="!['group-title'].includes(schema.type) && schema.collapse" >
        <b-collapse
          v-if="collapseId"
          :id="collapseId"
          @shown="collapsableVisibilityChanged"
          @hidden="collapsableVisibilityChanged" >
          <slot />
        </b-collapse>
      </template>
      <template
        v-else-if="!['group-title'].includes(schema.type)">
        <!-- Elements wrapped in toggle box -->
        <template v-if="wrapInToggle">
          <div class="toggle-wrapper" >
            <div
              class="toggle-title-wrapper flex flex-align-center show-hand"
              @click="toggleSlot = !toggleSlot" >
              <h3 class="greedy">{{ schema.toggleTitle }}</h3>
              <SwitchCheckbox
                v-model="toggleSlot"
                :height="20"
                :radius="7"
                :gap="3"
                outline-color="#cee7ff"
                switch-color="#2680eb"
                off-outline-color="#e6ebed"
                off-switch-color="#9eafb5"
                @click.native.stop />
            </div>
            <b-collapse
              v-model="toggleSlot"
              @shown="collapsableVisibilityChanged"
              @hidden="collapsableVisibilityChanged" >
              <div>
                <slot />
              </div>
            </b-collapse>
          </div>
        </template>
        <!-- Regular elements -->
        <template v-else>
          <slot />
        </template>
      </template>
    </div>

    <!-- Custom validator message -->
    <div
      v-if="customErrorMessage && propName && customErrorMessage[propName] && customErrorMessage[propName].dirty"
      class="error flex flex-align-center" >
      <SBIcon
        class="mr-1"
        :icon="SBIconList.ERROR"
        :color="SBColors.redPrimary"
        :size="10" />
      <span v-html="customErrorMessage[propName].message" />
    </div>
    <!-- Subtitle prefix -->
    <div
      v-else-if="schema.subtitleBottom"
      class="subtitle subtitle-bottom flex flex-align-center"
      :class="{ 'conversation-indent': !wrapInToggle && schema.type === 'conversation' }" >
      <img
        v-if="schema.subtitleBottomIcon && typeof schema.subtitleBottomIcon === 'string'"
        :src="schema.subtitleBottomIcon"
        style="width: 10px; height: 10px;"
        class="mr-1"
      >
      <SBIcon
        v-else-if="!schema.noSubtitlePrefixIcon"
        class="mr-1"
        :icon="SBIconList.INFO"
        :color="SBColors.greyLight"
        :size="10" />
      <span>{{ schema.subtitleBottom }}</span>
    </div>
    <span
      v-if="schema.bottomSpacer"
      :style="{ display: 'block', height: `${schema.bottomSpacer}px` }" />

    <!-- Info modal -->
    <InfoModal
      v-if="schema.modalInfo"
      ref="infoModal"
      :data="schema.modalInfo" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import randomString from '@/helpers/randomString';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import DotSeparator from '@/components/common/DotSeparator';
import InfoModal from '../../forms_schema/modals/InfoModal';
import SwitchCheckbox from '../../common/SwitchCheckbox.v2';

export default {
  name: 'SBCustomWrapperJSONForms',
  components: {
    SBIcon,
    InfoModal,
    DotSeparator,
    SwitchCheckbox,
  },
  props: {
    title: {
      type: [String, null],
      required: false,
      default: null,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    propName: {
      type: String,
      default: '',
    },
    customErrorMessage: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      randomId: randomString(),
      collapseId: '',
      collapsed: false,
      count: null,
      total: null,
      hideMe: false,
      toggleSlot: false,
      keyReset: 0,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    styleProps() {
      let styles = {};
      if (this.schema.width && this.schema.width > 0) {
        styles = {
          flexBasis: `calc(${this.schema.width}% - 10px)`, // it is important to have 10px padding which matches the gap between the columns. The last gap will be removed anyway and the columns will stretch due to the flex-grow property.
          flexGrow: this.schema.width,
          width: '1px',
        };
      }
      if (this.schema.color) {
        styles = {
          ...styles,
          '--background-color': this.schema.color,
        };
      } else {
        styles = {
          ...styles,
          '--background-color': this.schema.color || this.SBColors.greyUltralight,
        };
      }
      return styles;
    },
    renderMe() {
      if (this.hideMe || (['remoteData'].includes(this.schema.type) && !this.schema.endpoint)) {
        return false;
      }
      return true;
    },
    wrapInToggle() {
      return Object.hasOwn(this.schema, 'toggle');
    },
  },
  watch: {
    toggleSlot(val) {
      if (this.schema.type === 'object') {
        try {
          if (val === this.schema.toggle) {
            this.removeToggleElement(this.propName);
          } else {
            this.setToggleElement({
              name: this.propName,
              state: val,
            });
          }
          // set child element toggle state
          const targetChild = this.$children.find(child => child.$el.classList.contains('collapse'));
          const formChild = targetChild.$children.find(child => child.$el.classList.contains('json-schema-form'));

          if (formChild) {
            formChild.$children.forEach((child) => {
              child.$children[0].setToggle(val);
            });
          }
        } catch (e) {
          // toggle not implemented
        }
      } else {
        try {
          // set child element toggle state
          const targetChild = this.$children.find(child => child.$el.classList.contains('collapse'));
          targetChild.$children[0].setToggle(val);
        } catch (e) {
          // toggle not implemented
        }
      }
    },
  },
  created() {
    this.collapseId = `data-collapse-${this.randomId}`;
  },
  mounted() {
    this.$root.$on('bv::collapse::state', (id, state) => {
      if (id === this.collapseId) {
        this.collapsed = state;
      }
    });
    if (this.wrapInToggle) {
      this.toggleSlot = this.schema.toggle;

      // const targetChild = this.$children.find(child => child.$el.classList.contains('collapse'));
      // const formChild = targetChild.$children.find(child => child.$el.classList.contains('json-schema-form'));

      // if (formChild) {
      //   formChild.$children.forEach((child) => {
      //     child.$children[0].$on('input', (val) => {
      //       // this.toggleSlot = val;
      //     });
      //   });
      // }
    } else {
      // set child toggles to true just for the validation to work
      const formChild = this.$children ? this.$children.find(child => child.$el.classList && child.$el.classList.contains('json-schema-form')) : null;

      if (formChild) {
        formChild.$children.forEach((child) => {
          try {
            child.$children[0].setToggle(true);
          } catch (e) { }
        });
      }
    }
  },
  methods: {
    ...mapActions({
      setToggleElement: 'flows/setToggleElement',
      removeToggleElement: 'flows/removeToggleElement',
    }),
    collapsableVisibilityChanged() {
      window.eventBus.$emit('height-changed');
    },
    showInfoModal() {
      this.$refs.infoModal.show();
    },
    setChildData(data) {
      if (data && data.count) {
        this.count = data.count;
      }
      if (data && data.total) {
        this.total = data.total;
      }
      if (data && data.hideMe) {
        this.hideMe = data.hideMe;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-wrapper {
  &.half-top-gap {
    margin-top: -10px;
  }

  &.no-top-gap {
    margin-top: -18px;
  }

  .title, .title p {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 500;
    color: $blackPrimary;
    line-height: normal !important;

    &.normal {
      font-weight: 400;
    }

    &.no-margin {
      margin: 0;
    }

    &.collapsable {
      cursor: pointer;
    }

    &.not-collapsable {
      cursor: default !important;
    }
  }

  .title p {
    margin: 0 !important;
    .lighter {
      color: $blackUltralight;
    }
  }

  .modal-info {
    margin: 0 0 8px !important;
    * {
      font-size: 14px !important;
      color: $bluePrimary !important;
      line-height: normal !important;
    }

    &:hover * {
      color: $blackPrimary !important;
    }
  }

  .optional {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 500;
    text-align: right;
    color: $blackUltralight;
    line-height: normal !important;
  }

  // SB-NOTE - coloring the svg based on color value
  ::v-deep .external-svg path {
    fill: currentColor;
  }

  .toggle-wrapper {
    border-radius: 10px;
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px $greyBorder;
    // overflow: hidden;

    .toggle-title-wrapper {
      height: 32px;
      padding-left: 12px;
      padding-right: 12px;

      * {
        font-size: 14px !important;
        color: $blackPrimary !important;
        line-height: normal !important;
        font-weight: 500 !important;
      }
    }

    .collapse {
      background-color: $greyUltralight;
      border-top: solid 1px $greyBorderModal;
      border-radius: 0 0 9px 9px;

      & > div {
        padding: 17px 12px 18px;
      }
    }
  }
}
</style>
